// src/pages/home/Donate.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaUniversity, FaQrcode, FaHandHoldingHeart } from "react-icons/fa";

const DEFAULT_HERO = {
  heading: "Support Our Champions",
  subheading:
    "Your contribution fuels dreams and transforms lives, empowering the next generation of para-athletes in Andhra Pradesh.",
  image: "",
};

const DEFAULT_WHY_META = {
  heading: "Why Your Donation Matters",
  subheading:
    "Behind every medal is a story of struggle and perseverance. Your support removes financial barriers and provides our athletes with the resources they need to succeed.",
};

const DEFAULT_WHY_CARDS = [
  {
    heading: "Equipment & Kits",
    body: "Fund specialized equipment, sportswear, and training kits for athletes.",
  },
  {
    heading: "Travel & Accommodation",
    body: "Help athletes attend national and international competitions without financial stress.",
  },
  {
    heading: "Training & Coaching",
    body: "Support coaching camps, nutrition, and access to the best training facilities.",
  },
];

const DEFAULT_BANK_DETAILS = {
  account_name: "PARA SPORTS ASSOCIATION AP",
  account_number: "356220110000100",
  ifsc_code: "BKID0005622",
  bank_name: "BANK OF INDIA, MVP Double Road, Visakhapatnam",
  note: 'Please mention "Donation" in the transaction remarks. All contributions are deeply appreciated.',
};

const DEFAULT_UPI = {
  upi_id: "parasportsap@upi",
  instruction: "Scan this code with any UPI app like Google Pay, PhonePe, or Paytm to donate instantly.",
  qr_image: "/images/image-fallback.svg",
};

const normalizeSectionKey = (value) =>
  String(value || "").trim().toLowerCase();

const normalizeBodyLabelKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeBodyText = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\r\n?/g, "\n");

const parseBodyKeyValues = (bodyText) => {
  const text = normalizeBodyText(bodyText).trim();
  if (!text) return {};

  const result = {};
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  lines.forEach((line) => {
    const idx = line.indexOf(":");
    if (idx <= 0) return;
    const key = normalizeBodyLabelKey(line.slice(0, idx).replace(/^[-*•\d.)\s]+/, ""));
    const value = line.slice(idx + 1).trim();
    if (!value) return;
    result[key] = value;
  });

  // Supports single-line formats like:
  // Account Name: ..., Account Number: ..., IFSC Code: ...
  const pairPattern =
    /(?:^|[\n\r]|[;,|])\s*(?:[-*•]|\d+[.)])?\s*([A-Za-z][A-Za-z0-9 _-]{0,60})\s*:/g;
  const matches = Array.from(text.matchAll(pairPattern));

  if (matches.length > 1) {
    matches.forEach((match, index) => {
      const key = normalizeBodyLabelKey(match[1]);
      if (!key) return;

      const valueStart = (match.index || 0) + match[0].length;
      const valueEnd = index + 1 < matches.length ? (matches[index + 1].index || text.length) : text.length;
      const value = text
        .slice(valueStart, valueEnd)
        .replace(/^[,\s]+|[,\s]+$/g, "")
        .trim();
      if (!value) return;
      result[key] = value;
    });
  }

  return result;
};

const Donate = () => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await API.fetchAboutSections("donate");
        if (!mounted) return;
        setSections(Array.isArray(data) ? data : []);
      } catch (_error) {
        if (!mounted) return;
        setSections([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const sectionMap = useMemo(() => {
    const map = {};
    (Array.isArray(sections) ? sections : [])
      .filter((section) => section?.is_active !== false)
      .forEach((section) => {
        map[normalizeSectionKey(section.section_key)] = section;
      });
    return map;
  }, [sections]);

  const sortedSections = useMemo(
    () =>
      (Array.isArray(sections) ? sections : [])
        .filter((section) => section?.is_active !== false)
        .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)),
    [sections]
  );

  const whyCards = useMemo(() => {
    const cards = sortedSections
      .filter((section) => normalizeSectionKey(section.section_key).startsWith("donate_why_card_"))
      .map((section) => ({
        heading: section.heading || "",
        body: section.body || "",
      }))
      .filter((card) => card.heading || card.body);

    return cards.length ? cards : DEFAULT_WHY_CARDS;
  }, [sortedSections]);

  const heroSection = sectionMap["donate_hero"] || null;
  const whyMetaSection = sectionMap["donate_why_meta"] || null;
  const waysMetaSection = sectionMap["donate_ways_meta"] || null;
  const bankSection = sectionMap["donate_bank"] || null;
  const upiSection = sectionMap["donate_upi"] || null;

  const heroHeading = (heroSection?.heading || "").trim() || DEFAULT_HERO.heading;
  const heroSubheading = (heroSection?.subheading || "").trim() || DEFAULT_HERO.subheading;
  // eslint-disable-next-line no-unused-vars
  const heroImage = heroSection?.image_path
    ? resolveAssetUrl(heroSection.image_path, { folder: "page-sections", defaultPath: DEFAULT_HERO.image })
    : DEFAULT_HERO.image;

  const whyHeading = (whyMetaSection?.heading || "").trim() || DEFAULT_WHY_META.heading;
  const whySubheading = (whyMetaSection?.subheading || "").trim() || DEFAULT_WHY_META.subheading;
  const waysHeading = (waysMetaSection?.heading || "").trim() || "Ways to Contribute";

  const bankMeta = parseBodyKeyValues(bankSection?.body || "");
  const bankDetails = {
    account_name:
      bankMeta["account name"] || bankMeta["accountname"] || DEFAULT_BANK_DETAILS.account_name,
    account_number:
      bankMeta["account number"] || bankMeta["accountnumber"] || DEFAULT_BANK_DETAILS.account_number,
    ifsc_code:
      bankMeta["ifsc code"] || bankMeta["ifsc"] || bankMeta["ifsccode"] || DEFAULT_BANK_DETAILS.ifsc_code,
    bank_name: bankMeta["bank name"] || bankMeta["bankname"] || DEFAULT_BANK_DETAILS.bank_name,
    note: bankMeta["note"] || bankSection?.subheading || DEFAULT_BANK_DETAILS.note,
  };

  const upiMeta = parseBodyKeyValues(upiSection?.body || "");
  const upiId =
    upiMeta["upi id"] ||
    upiMeta["upiid"] ||
    (upiSection?.body || "").trim() ||
    DEFAULT_UPI.upi_id;
  const upiInstruction = (upiSection?.subheading || "").trim() || DEFAULT_UPI.instruction;
  const upiQrImage = upiSection?.image_path
    ? resolveAssetUrl(upiSection.image_path, { folder: "page-sections", defaultPath: DEFAULT_UPI.qr_image })
    : DEFAULT_UPI.qr_image;

  return (
    <div className="bg-slate-50">
      <Navbar />

      <main>
        {/* ===== HERO SECTION ===== */}
        <section className="relative h-[30vh] flex items-center justify-center text-center text-white px-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-800 via-blue-700 to-indigo-800" />
          <div className="relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
              {heroHeading}
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-90 mt-4">
              {heroSubheading}
            </p>
          </div>
        </section>
        
        {/* ===== WHY DONATE SECTION ===== */}
        <section className="py-20 bg-white">
          <div className="max-w-screen-lg mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900">{whyHeading}</h2>
              <p className="text-lg text-slate-600 mt-3 max-w-3xl mx-auto">
                {whySubheading}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {whyCards.map((card, idx) => (
                <div className="p-6" key={`${card.heading || "why-card"}-${idx}`}>
                  <FaHandHoldingHeart className="mx-auto text-yellow-500 text-5xl mb-4" />
                  <h3 className="text-xl font-bold text-slate-800">{card.heading || "-"}</h3>
                  <p className="text-slate-600 mt-2">{card.body || ""}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== WAYS TO DONATE SECTION ===== */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-screen-lg mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900">{waysHeading}</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

              {/* Bank Transfer Details */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                <div className="flex items-center gap-4 mb-6">
                  <FaUniversity className="text-blue-600 text-4xl flex-shrink-0" />
                  <h3 className="text-3xl font-bold text-slate-800">
                    {(bankSection?.heading || "").trim() || "Direct Bank Transfer"}
                  </h3>
                </div>
                <div className="space-y-4 text-lg">
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm font-semibold text-slate-500">Account Name</p>
                    <p className="font-mono text-slate-900">{bankDetails.account_name}</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm font-semibold text-slate-500">Account Number</p>
                    <p className="font-mono text-slate-900">{bankDetails.account_number}</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm font-semibold text-slate-500">IFSC Code</p>
                    <p className="font-mono text-slate-900">{bankDetails.ifsc_code}</p>
                  </div>
                   <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm font-semibold text-slate-500">Bank Name</p>
                    <p className="font-mono text-slate-900">{bankDetails.bank_name}</p>
                  </div>
                </div>
                 <p className="text-sm text-slate-500 mt-6">
                  {bankDetails.note}
                </p>
              </div>

              {/* UPI/QR Code */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 text-center">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <FaQrcode className="text-blue-600 text-4xl" />
                  <h3 className="text-3xl font-bold text-slate-800">
                    {(upiSection?.heading || "").trim() || "Scan & Pay (UPI)"}
                  </h3>
                </div>
                <div className="flex justify-center">
                    <div className="p-4 border-4 border-blue-600 rounded-xl inline-block">
                        <img 
                            src={upiQrImage}
                            alt="UPI QR Code for Donation"
                            className="w-64 h-64"
                        />
                    </div>
                </div>
                <p className="font-mono text-slate-800 text-lg mt-4">
                  {upiId}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  {upiInstruction}
                </p>
              </div>
            </div>
          </div>
        </section>
        <PageSectionsRenderer
          pageKey="donate"
          className="pt-0"
          excludeKeys={["donate_hero", "donate_why_meta", "donate_ways_meta", "donate_bank", "donate_upi"]}
          excludeKeyPrefixes={["donate_why_card_"]}
        />
      </main>

      <Footer />
    </div>
  );
};

export default Donate;

